import { useState, useEffect } from 'react';
import API from '../api/axios';
import { useToast } from '../context/ToastContext';
import './SMEStyles.css';

const ModeratorApprovals = () => {
  const { showToast } = useToast();

  const [requests, setRequests] = useState([]);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [papers, setPapers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingPapers, setLoadingPapers] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [downloading, setDownloading] = useState({});

  useEffect(() => {
    fetchApprovedRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchApprovedRequests = async () => {
    try {
      setLoading(true);
      const { data } = await API.get('/moderator-categorization/approved-requests');
      setRequests(data.requests || []);
    } catch (err) {
      showToast('Failed to fetch approved requests', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleViewPapers = async (request) => {
    try {
      setLoadingPapers(true);
      setSelectedRequest(request);
      const { data } = await API.get(`/moderator-categorization/request/${request.request_id}/approved-papers`);
      setPapers(data.papers || []);
    } catch (err) {
      showToast('Failed to load papers', 'error');
    } finally {
      setLoadingPapers(false);
    }
  };

  const handleBack = () => {
    setSelectedRequest(null);
    setPapers([]);
  };

  const handleDownloadPDF = async (paperId, category) => {
    try {
      setDownloading({ ...downloading, [paperId]: true });

      const response = await API.get(`/moderator-categorization/paper/${paperId}/download`, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `paper_${paperId}_${category}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      showToast('PDF downloaded successfully!', 'success');
    } catch (err) {
      showToast('Failed to download PDF', 'error');
    } finally {
      setDownloading({ ...downloading, [paperId]: false });
    }
  };

  const getCategoryBadgeClass = (category) => {
    const badges = {
      general: 'sme-badge--primary',
      reexam: 'sme-badge--warning',
      special: 'sme-badge--info'
    };
    return badges[category] || 'sme-badge--neutral';
  };

  const getCategoryLabel = (category) => {
    const labels = {
      general: 'General Exam',
      reexam: 'Re-Exam',
      special: 'Special Case'
    };
    return labels[category] || category;
  };

  const filteredRequests = requests.filter(r => {
    const matchesSearch = searchTerm === '' ||
      r.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
      r.topic.toLowerCase().includes(searchTerm.toLowerCase()) ||
      r.faculty_name.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  // Papers detail view
  if (selectedRequest) {
    return (
      <div className="sme-page">
        <div className="sme-page-header">
          <div>
            <button className="sme-btn sme-btn--secondary" onClick={handleBack} style={{ marginBottom: '0.75rem' }}>
              Back to Requests
            </button>
            <h1>Approved Papers</h1>
            <p>{selectedRequest.subject} - {selectedRequest.topic}</p>
          </div>
        </div>

        {loadingPapers ? (
          <div className="sme-spinner-wrap">
            <div className="sme-spinner" />
          </div>
        ) : papers.length === 0 ? (
          <div className="sme-card">
            <div className="sme-empty">
              <h3>No papers found</h3>
              <p>This request has no categorized papers yet.</p>
            </div>
          </div>
        ) : (
          <div className="sme-card-grid">
            {papers.map(paper => (
              <div key={paper.paper_id} className="sme-card">
                <div className="sme-card-header">
                  <div>
                    <h2>Set {paper.set_number} - {getCategoryLabel(paper.category)}</h2>
                    <p>{paper.question_count} questions &bull; {paper.total_marks} marks</p>
                    {paper.selection_reason && (
                      <p style={{ fontStyle: 'italic' }}>SME: "{paper.selection_reason}"</p>
                    )}
                  </div>
                  <span className={`sme-badge ${getCategoryBadgeClass(paper.category)}`}>
                    {getCategoryLabel(paper.category)}
                  </span>
                </div>

                <div className="sme-card-body">
                  {paper.moderator_notes && (
                    <div className="sme-detail-box">
                      <label className="sme-field-label">Moderator Notes</label>
                      <p className="sme-field-value" style={{ fontWeight: 400 }}>{paper.moderator_notes}</p>
                    </div>
                  )}

                  <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
                    <button
                      className="sme-btn sme-btn--primary"
                      onClick={() => handleDownloadPDF(paper.paper_id, paper.category)}
                      disabled={downloading[paper.paper_id]}
                    >
                      {downloading[paper.paper_id] ? 'Downloading...' : 'Download PDF'}
                    </button>
                    <span className="sme-muted" style={{ fontSize: '0.82rem' }}>
                      Approved on {new Date(paper.categorized_at).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // List view
  return (
    <div className="sme-page">
      <div className="sme-page-header">
        <div>
          <h1>Approved Requests</h1>
          <p>View all completed and categorized paper sets</p>
        </div>
      </div>

      <div className="sme-stats-grid">
        <div className="sme-stat-card">
          <div className="sme-stat-icon sme-stat-icon--green">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 11.08V12a10 10 0 11-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" />
            </svg>
          </div>
          <div>
            <div className="sme-stat-value">{requests.length}</div>
            <div className="sme-stat-label">Completed Requests</div>
          </div>
        </div>
        <div className="sme-stat-card">
          <div className="sme-stat-icon sme-stat-icon--blue">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" /><polyline points="14 2 14 8 20 8" />
            </svg>
          </div>
          <div>
            <div className="sme-stat-value">{requests.reduce((sum, r) => sum + r.categorized_count, 0)}</div>
            <div className="sme-stat-label">Total Papers</div>
          </div>
        </div>
      </div>

      <div className="sme-card">
        <div className="sme-card-header">
          <h2>Completed Requests</h2>
          <span className="sme-badge sme-badge--primary">{filteredRequests.length}</span>
        </div>
        <div className="sme-card-body" style={{ paddingBottom: 0 }}>
          <div className="sme-form-group">
            <label className="sme-form-label">Search</label>
            <input
              type="text"
              className="sme-form-input"
              placeholder="Search by subject, topic, or faculty..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        {loading ? (
          <div className="sme-spinner-wrap">
            <div className="sme-spinner" />
          </div>
        ) : filteredRequests.length === 0 ? (
          <div className="sme-empty">
            <h3>No completed requests found</h3>
            <p>Categorized paper sets will appear here once requests are completed.</p>
          </div>
        ) : (
          <div className="sme-table-wrap">
            <table className="sme-table">
              <thead>
                <tr>
                  <th>Request</th>
                  <th>Subject / Topic</th>
                  <th>Faculty</th>
                  <th>SME</th>
                  <th>Papers</th>
                  <th>Completed</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredRequests.map(request => (
                  <tr key={request.request_id}>
                    <td><span className="sme-code-tag">#{request.request_id}</span></td>
                    <td>
                      <div className="sme-td-name">{request.subject}</div>
                      <div className="sme-muted" style={{ fontSize: '0.82rem' }}>{request.topic}</div>
                    </td>
                    <td className="sme-muted">{request.faculty_name}</td>
                    <td className="sme-muted">{request.sme_name || 'N/A'}</td>
                    <td>
                      <span className="sme-badge sme-badge--success">{request.categorized_count} categorized</span>
                    </td>
                    <td className="sme-muted">{new Date(request.completed_at).toLocaleDateString()}</td>
                    <td>
                      <button className="sme-btn sme-btn--sm sme-btn--primary" onClick={() => handleViewPapers(request)}>
                        View Papers
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ModeratorApprovals;
